import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, BadgeCheck, Star, Building2, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1483683804023-6ccdb62f86ef?w=1920&q=85";

export default function Agents() {
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get("/agents/leaderboard").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
    }, []);

    return (
        <div data-testid="agents-page">
            <section className="relative h-[40vh] flex items-end overflow-hidden">
                <img src={HERO_BG} alt="Fiji" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ocean-deep via-ocean-deep/70 to-transparent" />
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
                    <div className="text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold mb-2">Agents</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold text-white max-w-3xl leading-tight">
                        Fiji's most active land & Joint Venture agents
                    </h1>
                    <p className="text-white/85 font-app text-lg mt-3 max-w-2xl">
                        Verified agents with the most active listings. Reach out direct or browse their portfolio.
                    </p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {items === null && (
                    <Card className="p-12 text-center">
                        <p className="font-app text-muted-foreground">Loading top agents…</p>
                    </Card>
                )}

                {items?.length === 0 && (
                    <Card className="p-12 text-center">
                        <Trophy className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                        <p className="font-app text-muted-foreground">
                            No agent listings yet. Are you an agent? <Link to="/register" className="text-coral hover:underline">Register</Link> and start uploading.
                        </p>
                    </Card>
                )}

                {items?.length > 0 && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {items.map((a, i) => (
                            <Card key={a.id} data-testid={`agent-card-${a.id}`} className="p-5 hover:shadow-xl transition-shadow">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-fiji-green to-ocean text-white grid place-items-center font-display font-bold flex-shrink-0">
                                        {(a.full_name || "?").slice(0, 1).toUpperCase()}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <h3 className="font-display text-base font-bold truncate">{a.full_name}</h3>
                                            {a.agent_verified && (
                                                <Badge className="bg-fiji-green/15 text-fiji-green border-0 text-[10px] uppercase font-app">
                                                    <BadgeCheck className="h-3 w-3 mr-0.5" /> Verified
                                                </Badge>
                                            )}
                                            {i === 0 && a.active_listings > 0 && (
                                                <Badge className="bg-coral/15 text-coral border-0 text-[10px] uppercase font-app">
                                                    <Trophy className="h-3 w-3 mr-0.5" /> #1
                                                </Badge>
                                            )}
                                        </div>
                                        {a.agency_name && (
                                            <div className="text-xs font-app text-muted-foreground truncate">{a.agency_name}</div>
                                        )}
                                    </div>
                                </div>
                                {a.agent_bio && (
                                    <p className="text-sm font-app text-muted-foreground line-clamp-2 mb-4">{a.agent_bio}</p>
                                )}
                                <div className="flex items-center gap-4 text-xs font-app text-muted-foreground mb-4">
                                    <div className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> <span className="font-display font-bold text-foreground">{a.active_listings}</span> listing{a.active_listings === 1 ? "" : "s"}</div>
                                    {a.featured_listings > 0 && (
                                        <div className="flex items-center gap-1 text-coral"><Star className="h-3.5 w-3.5" /> {a.featured_listings} featured</div>
                                    )}
                                </div>
                                <Button asChild variant="outline" size="sm" className="w-full" data-testid={`view-agent-${a.id}`}>
                                    <Link to={`/agents/${a.id}`}>View portfolio <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
                                </Button>
                            </Card>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
